import React, { useRef, useState } from "react";
import { ActionNodeMenu } from "./action-node";
import { ChevronDown, XIcon } from "lucide-react";
import { PlusIcon, Delete } from "lucide-react";
import { LeftDot, RightDot } from "../../../shared/action-core";
import { DeleteModal } from "./delete-action";


interface CardHeaderProps {
  title: string;
  description?: React.ReactNode;
  onDelete: () => void;
  onReset: () => void;
}

export const CardHeader: React.FC<CardHeaderProps> = ({
  title,
  description,
  onDelete,
  onReset,
}) => (
  <>
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs font-medium text-[#7B7B7B] font-semibold dark:text-gray-500 uppercase tracking-wide bg-indigo-50 rounded px-1.5 py-0.5">
        Action
      </span>
      <ActionNodeMenu onDelete={onDelete} onReset={onReset} />
    </div>
    <p className="text-indigo-600 flex gap-1 font-semibold text-sm mb-0.5">
      {title}
      <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M6.66667 0C10.3487 0 13.3333 2.98467 13.3333 6.66667C13.3333 10.3487 10.3487 13.3333 6.66667 13.3333C2.98467 13.3333 0 10.3487 0 6.66667C0 2.98467 2.98467 0 6.66667 0ZM6.66667 1.33333C5.25218 1.33333 3.89562 1.89524 2.89543 2.89543C1.89524 3.89562 1.33333 5.25218 1.33333 6.66667C1.33333 8.08115 1.89524 9.43771 2.89543 10.4379C3.89562 11.4381 5.25218 12 6.66667 12C8.08115 12 9.43771 11.4381 10.4379 10.4379C11.4381 9.43771 12 8.08115 12 6.66667C12 5.25218 11.4381 3.89562 10.4379 2.89543C9.43771 1.89524 8.08115 1.33333 6.66667 1.33333ZM6.66667 9.33333C6.84348 9.33333 7.01305 9.40357 7.13807 9.52859C7.2631 9.65362 7.33333 9.82319 7.33333 10C7.33333 10.1768 7.2631 10.3464 7.13807 10.4714C7.01305 10.5964 6.84348 10.6667 6.66667 10.6667C6.48986 10.6667 6.32029 10.5964 6.19526 10.4714C6.07024 10.3464 6 10.1768 6 10C6 9.82319 6.07024 9.65362 6.19526 9.52859C6.32029 9.40357 6.48986 9.33333 6.66667 9.33333ZM6.66667 3C7.22819 3.00002 7.77218 3.19557 8.20519 3.55307C8.6382 3.91056 8.93321 4.40769 9.03954 4.95905C9.14587 5.51041 9.05688 6.08159 8.78786 6.57447C8.51884 7.06736 8.08658 7.45118 7.56533 7.66C7.48813 7.68839 7.41854 7.73425 7.362 7.794C7.33267 7.82733 7.328 7.87 7.32867 7.914L7.33333 8C7.33315 8.16992 7.26808 8.33335 7.15143 8.45691C7.03479 8.58047 6.87536 8.65482 6.70574 8.66478C6.53611 8.67474 6.36908 8.61955 6.23878 8.51049C6.10848 8.40143 6.02474 8.24673 6.00467 8.078L6 8V7.83333C6 7.06467 6.62 6.60333 7.06933 6.42267C7.25221 6.34964 7.41174 6.22818 7.53079 6.07132C7.64984 5.91447 7.72392 5.72815 7.74506 5.53237C7.76621 5.33659 7.73363 5.13875 7.65082 4.96009C7.568 4.78144 7.43809 4.62871 7.27503 4.51832C7.11196 4.40793 6.92191 4.34404 6.72527 4.33351C6.52863 4.32299 6.33285 4.36623 6.15893 4.45858C5.98501 4.55094 5.83954 4.68892 5.73813 4.85772C5.63672 5.02652 5.58321 5.21975 5.58333 5.41667C5.58333 5.59348 5.5131 5.76305 5.38807 5.88807C5.26305 6.0131 5.09348 6.08333 4.91667 6.08333C4.73986 6.08333 4.57029 6.0131 4.44526 5.88807C4.32024 5.76305 4.25 5.59348 4.25 5.41667C4.25 4.77573 4.50461 4.16104 4.95783 3.70783C5.41104 3.25461 6.02573 3 6.66667 3Z" fill="#4338CA" />
      </svg>

    </p>
    {description && <p className="text-xs text-[#7B7B7B] font-semibold dark:text-gray-500 mb-4">{description}</p>}
  </>
);

// Shared canvas-card chrome (border/dots/header/delete-confirm) for every
// action type — pass `bare` when rendering the same fields inside the
// right-side config sidebar instead of on the canvas, which supplies its
// own header/border, so this just renders the fields with no wrapper.
export const CardShell: React.FC<{
  title: string;
  description?: React.ReactNode;
  onDelete: () => void;
  onReset: () => void;
  children: React.ReactNode;
  bare?: boolean;
}> = ({ title, description, onDelete, onReset, children, bare }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  if (bare) return <>{children}</>;

  return (
    <>
      <div className="relative bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-4 w-[260px]">
        <LeftDot />
        <RightDot />
        <CardHeader title={title} description={description} onDelete={() => setShowDeleteModal(true)} onReset={onReset} />
        {children}
      </div>
      {showDeleteModal && (
        <DeleteModal
          title="Delete action?"
          description="This action will be removed from the flow and won't run when the trigger is activated."
          confirmLabel="Delete Action"
          onConfirm={() => { onDelete(); setShowDeleteModal(false); }}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </>
  );
};



interface BasicSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { label: string; value: string }[];
}

export const BasicSelect: React.FC<BasicSelectProps> = ({ value, onChange, options }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 text-sm appearance-none bg-white dark:bg-gray-900 pr-8 focus:outline-none focus:ring-1 focus:ring-indigo-300"
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <ChevronDown size={16} className="absolute right-2 top-2.5 text-gray-500 dark:text-gray-400 pointer-events-none" />
  </div>
);


export const TagPill = ({ label, onRemove }: { label: string; onRemove: () => void }) => (
  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium border bg-indigo-50 border-indigo-200 text-indigo-700">
    {label}
    <button onClick={onRemove} className="ml-0.5 hover:opacity-70">
      <XIcon size={12} />
    </button>
  </span>
);



type MediaType = "image" | "audio" | "video" | "document" | null;

const ImageIcon = () => (
  <svg viewBox="0 0 24 24" className="w-8 h-8 fill-none stroke-gray-300 stroke-1.5">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const ACCEPTED = "image/*,audio/*,video/*,application/pdf";

function detectMediaType(file: File): MediaType {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("audio/")) return "audio";
  if (file.type.startsWith("video/")) return "video";
  if (file.type === "application/pdf") return "document";
  return "document";
}

interface MediaUploadWidgetProps {
  mediaUrl: string | null;
  mediaType: MediaType;
  onUploaded: (file: File, type: MediaType) => Promise<void> | void;
  onRemove: () => void;
}

export const MediaUploadWidget: React.FC<MediaUploadWidgetProps> = ({
  mediaUrl,
  mediaType,
  onUploaded,
  onRemove,
}) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const type = detectMediaType(file);

    if (file.type.startsWith("image/")) {
      const objectUrl = URL.createObjectURL(file);
      setPreview(objectUrl);
    } else {
      setPreview(null);
    }

    try {
      setUploading(true);
      await onUploaded(file, type);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onRemove();
  };

  if (mediaUrl) {
    return (
      <div className="mb-3">
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Upload Media (images, audio, video or PDF)</p>
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          {mediaType === "image" && (preview || mediaUrl) && (
            <img src={preview ?? mediaUrl} alt="preview" className="w-full h-28 object-cover" />
          )}
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800">
            <ImageIcon />
            <span className="text-xs text-gray-600 dark:text-gray-300 truncate flex-1">{mediaUrl}</span>
          </div>
          <div className="flex justify-end px-3 py-2 border-t">
            <button onClick={handleRemove} className="text-red-400 hover:text-red-600">
              <Delete size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-1.5">Upload Media (images, audio, video or PDF)</p>
      <input ref={fileRef} type="file" accept={ACCEPTED} className="hidden" onChange={handleFileChange} />
      <button
        onClick={() => fileRef.current?.click()}
        disabled={uploading}
        className="flex items-center justify-center gap-1.5 border border-dashed border-gray-300 dark:border-gray-600 hover:border-indigo-400 rounded-lg px-3 py-2.5 text-sm text-gray-500 dark:text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-all w-full"
      >
        <PlusIcon size={16} />
        <span>{uploading ? "Uploading..." : "Upload Media"}</span>
      </button>
    </div>
  );
};

export const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{children}</label>
);
